const STORAGE_KEY = "steptember-motion";

export type MotionPreference = "system" | "on" | "off";

/**
 * Whether this reader sees animation.
 *
 * The operating system is the default answer and always will be — an animation nobody asked for
 * is the one that hurts. But "follow the system" is not the same as "the system decides forever":
 * plenty of people turn Windows animations off to keep menus snappy and would still like the
 * confetti on a celebration page. So the preference has three states, and only an explicit choice
 * here overrides `prefers-reduced-motion`.
 */
export function readMotionPreference(): MotionPreference {
  if (typeof window === "undefined") return "system";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === "on" || stored === "off" ? stored : "system";
}

export function writeMotionPreference(preference: MotionPreference) {
  if (typeof window === "undefined") return;
  if (preference === "system") window.localStorage.removeItem(STORAGE_KEY);
  else window.localStorage.setItem(STORAGE_KEY, preference);
  applyMotionPreference(preference);
}

/** Mirrors the choice onto the root element, where globals.css can act on it. */
export function applyMotionPreference(preference: MotionPreference = readMotionPreference()) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (preference === "system") delete root.dataset.motionPref;
  else root.dataset.motionPref = preference;
}

export function systemPrefersReducedMotion() {
  return (
    typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** The effective answer, resolving the reader's choice against the system setting. */
export function motionAllowed(preference: MotionPreference = readMotionPreference()) {
  if (preference === "on") return true;
  if (preference === "off") return false;
  return !systemPrefersReducedMotion();
}
